import { useEffect, type RefObject } from 'react';

/** Dark mode switches on once the trigger's middle has risen to this fraction of the viewport height */
const SWITCH_AT = 0.35;
/** …and back off once the end element's bottom edge has risen to this fraction (≈75% of it scrolled out) */
const END_AT = 0.7;
/** Blue takes over once the accent trigger's middle has risen to the vertical middle of the screen */
const ACCENT_AT = 0.5;

/**
 * Background sweep: once the trigger ("Let's make them feel something") reaches the switch point, the fixed
 * black backdrop fades in and every `[data-ink]` element flips to its dark-mode colours (`.ink-dark`).
 * Once the accent trigger (the statement before the work) has scrolled well up, a blue backdrop fades in over
 * the black. Once the end element (the project grid) has nearly scrolled out, it all fades back to light —
 * and everything reverses when scrolling up.
 */
export function useDarkSweep(
  triggerRef: RefObject<HTMLElement>,
  panelRef: RefObject<HTMLElement>,
  endRef?: RefObject<HTMLElement>,
  accentTriggerRef?: RefObject<HTMLElement>,
  accentPanelRef?: RefObject<HTMLElement>,
) {
  useEffect(() => {
    let raf = 0;
    let dark: boolean | null = null;
    let accent: boolean | null = null;

    // Only measured when the page actually scrolls or resizes (once per frame at most), not every frame
    const tick = () => {
      raf = 0;
      const trigger = triggerRef.current;
      const panel = panelRef.current;
      if (!trigger || !panel) return;

      const vh = window.innerHeight;
      const r = trigger.getBoundingClientRect();
      const started = r.top + r.height / 2 <= vh * SWITCH_AT;
      const end = endRef?.current;
      const ended = end ? end.getBoundingClientRect().bottom <= vh * END_AT : false;
      const nextDark = started && !ended;

      const at = accentTriggerRef?.current?.getBoundingClientRect();
      const nextAccent = nextDark && !!at && at.top + at.height / 2 <= vh * ACCENT_AT;
      if (nextAccent !== accent) {
        accent = nextAccent;
        if (accentPanelRef?.current) accentPanelRef.current.style.opacity = nextAccent ? '1' : '0';
      }

      if (nextDark === dark) return;
      dark = nextDark;
      panel.style.opacity = nextDark ? '1' : '0';
      document.querySelectorAll<HTMLElement>('[data-ink]').forEach((el) => el.classList.toggle('ink-dark', nextDark));
    };

    const schedule = () => {
      if (!raf) raf = requestAnimationFrame(tick);
    };
    schedule();
    // Late-loading content can shift the triggers, so re-check a few times after mount too
    const settle = [400, 1200, 3000].map((ms) => window.setTimeout(schedule, ms));
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    return () => {
      cancelAnimationFrame(raf);
      settle.forEach(clearTimeout);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      document.querySelectorAll('.ink-dark').forEach((el) => el.classList.remove('ink-dark'));
    };
  }, [triggerRef, panelRef, endRef, accentTriggerRef, accentPanelRef]);
}
